import {
  PageConfig,
  PageConfigBlock,
  PageConfigField,
  PageConfigSchemaVersion,
} from "../../config/pageConfig.types";
import { DefaultFormId } from "../../config/resolveView";
import { AudiencesWithTypes } from "../../hooks/lookupRecords";
import { fullWidth, halfWidth, thirdWidth } from "../../blocks/layout/columnSpans";
import {
  audienceRunAtRadioOptions,
  audienceTypeOptions,
} from "../communicationAudience/communicationAudienceRecord";
import {
  communicationAudienceTypeABOptions,
  communicationAudienceTypeOptions,
  communicationStartTypeOptions,
  communicationTargetedAtRadioOptions,
} from "./communicationOptions";

// Once published, a field is shown as text.
const lockedProps = (locked: boolean) =>
  locked ? { readOnly: true, showAsText: true } : {};

const idOf = (name: string, locked: boolean) =>
  `field-${name}${locked ? "-locked" : ""}`;

const audienceTypeBehaviour = {
  behaviour: "communicationStartTypeFromAudience",
  target: "g4c_starttype",
};

// Type, run-at and dynamic content are chosen until the communication is published, then shown as text.
const communicationTypeFields = (locked: boolean): PageConfigField[] => [
  {
    id: idOf("g4c_communicationtype", locked),
    type: "radio",
    name: "g4c_communicationtype",
    label: "Type",
    cards: true,
    inline: true,
    options: audienceTypeOptions,
    visibleWhen: locked ? "communicationPublished" : "communicationDraft",
    span: fullWidth,
    ...(locked ? { readOnly: true } : {}),
  },
  {
    id: idOf("RunAt", locked),
    type: "radio",
    name: "RunAt",
    label: "Run At",
    cards: true,
    inline: true,
    options: audienceRunAtRadioOptions,
    visibleWhen: locked ? "communicationRunAtPublished" : "communicationRunAtDraft",
    span: fullWidth,
    ...(locked ? { readOnly: true } : {}),
  },
];

const dynamicContentFields = (locked: boolean): PageConfigField[] => [
  {
    id: idOf("g4c_usedynamiccontent", locked),
    type: "boolean",
    name: "g4c_usedynamiccontent",
    label: "Dynamic Content",
    helpText:
      "If enabled, this option allows content to be shown dynamically based on field properties",
    visibleWhen: locked ? "communicationDynamicContentPublished" : "communicationDynamicContentDraft",
    span: fullWidth,
    ...lockedProps(locked),
  },
];

const audienceFields = (locked: boolean): PageConfigField[] => [
  // Chosen until the communication is first saved, then fixed.
  {
    id: idOf("g4c_listtype", locked),
    type: "radio",
    name: "g4c_listtype",
    label: "Targeted At",
    cards: true,
    inline: true,
    options: communicationTargetedAtRadioOptions,
    visibleWhen: locked ? "communicationTargetedAt" : "communicationTargetedAtChoice",
    span: fullWidth,
    ...(locked ? { readOnly: true } : {}),
  },
  ...(locked
    ? []
    : ([
        {
          id: "field-g4c_listtype-fixed",
          type: "radio",
          name: "g4c_listtype",
          label: "Targeted At",
          cards: true,
          inline: true,
          options: communicationTargetedAtRadioOptions,
          readOnly: true,
          visibleWhen: "communicationTargetedAtFixed",
          span: fullWidth,
        },
      ] as PageConfigField[])),
  {
    id: idOf("g4c_audiencetype-ab", locked),
    type: "picklist",
    name: "g4c_audiencetype",
    label: "Audience Type",
    required: true,
    options: communicationAudienceTypeABOptions,
    validatorKey: "communicationAudienceType",
    onChange: audienceTypeBehaviour,
    visibleWhen: "communicationAudienceWithAB",
    span: fullWidth,
    ...lockedProps(locked),
  },
  {
    id: idOf("g4c_audiencetype", locked),
    type: "picklist",
    name: "g4c_audiencetype",
    label: "Audience Type",
    required: true,
    options: communicationAudienceTypeOptions,
    validatorKey: "communicationAudienceType",
    onChange: audienceTypeBehaviour,
    visibleWhen: "communicationAudienceWithoutAB",
    span: fullWidth,
    ...lockedProps(locked),
  },
  // Once published the audience is a link to its record, which opens in a new tab.
  locked
    ? {
        id: idOf("g4c_audienceid", locked),
        type: "text",
        name: "g4c_audienceidname",
        label: "Audience",
        readOnly: true,
        link: { to: "/audience", idKey: "g4c_audienceid" },
        visibleWhen: "communicationExistingAudience",
        span: fullWidth,
      }
    : {
        id: idOf("g4c_audienceid", locked),
        type: "lookup",
        name: "g4c_audienceid",
        label: "Audience",
        required: true,
        lookupEntity: "g4c_communicationaudience",
        searchFilter: AudiencesWithTypes,
        searchable: true,
        optionFilterKey: "communicationAudience",
        validatorKey: "communicationAudienceLookup",
        visibleWhen: "communicationExistingAudience",
        span: fullWidth,
      },
  // Its rules are for choosing an audience, so a published communication does not show them.
  ...(locked
    ? []
    : ([
        {
          id: idOf("audience-summary", locked),
          type: "component",
          name: "audience_summary",
          label: "",
          componentKey: "communicationAudienceSummary",
          excludeFromSave: true,
          visibleWhen: "communicationAudienceSummary",
          span: fullWidth,
        },
      ] as PageConfigField[])),
];

const subjectField = (locked: boolean): PageConfigField =>
  locked
    ? {
        id: idOf("g4c_subject", locked),
        type: "text",
        name: "g4c_subject",
        label: "Email Subject Line",
        required: true,
        placeholder: "Add a subject",
        helpText: "Enter the subject line that will appear",
        span: fullWidth,
        ...lockedProps(locked),
      }
    : {
        id: idOf("g4c_subject", locked),
        type: "component",
        name: "g4c_subject",
        // The component draws its own label, beside the attribute button.
        label: "",
        componentKey: "communicationSubject",
        excludeFromSave: true,
        span: fullWidth,
      };

const setupFields = (locked: boolean): PageConfigField[] => [
  subjectField(locked),
  {
    id: idOf("FromName", locked),
    type: "text",
    name: "FromName",
    label: "From Name",
    required: true,
    placeholder: "Sender name",
    helpText: "The name your email is sent from",
    span: thirdWidth,
    ...lockedProps(locked),
  },
  {
    id: idOf("FromAddress", locked),
    type: "email",
    name: "FromAddress",
    label: "From Email Address",
    required: true,
    placeholder: "Sender email address",
    helpText: "The email address your email is sent from",
    validatorKey: "communicationFromAddress",
    span: thirdWidth,
    ...lockedProps(locked),
  },
  {
    id: idOf("ToName", locked),
    type: "text",
    name: "ToName",
    label: "Default Name",
    required: true,
    placeholder: "Fallback name, e.g. Customer",
    helpText: "The default name used when the To Name doesn't exist",
    span: thirdWidth,
    ...lockedProps(locked),
  },
];

const plainTextFields = (locked: boolean): PageConfigField[] => [
  {
    id: idOf("PlainTextContent", locked),
    type: "longtext",
    name: "PlainTextContent",
    label: "Plain Text Email Content",
    rows: 4,
    helpText: "Plain text version of the email, for clients which do not support HTML",
    span: fullWidth,
    ...lockedProps(locked),
  },
];

const publishFields = (locked: boolean): PageConfigField[] => [
  ...(locked
    ? []
    : ([
        {
          id: "content-ab-test-notice",
          kind: "content",
          type: "text",
          content:
            "This communication is an A/B Testing communication and must be published from the A/B Test. The expiry URL, start and end dates are also set from within the A/B test record.",
          span: fullWidth,
          visibleWhen: "communicationABTestNotice",
        },
      ] as PageConfigField[])),
  {
    id: idOf("g4c_starttype", locked),
    type: "picklist",
    name: "g4c_starttype",
    label: "When",
    required: true,
    options: communicationStartTypeOptions,
    visibleWhen: "communicationStartType",
    span: fullWidth,
    ...lockedProps(locked),
  },
  {
    id: idOf("g4c_startdate", locked),
    type: "datetime",
    name: "g4c_startdate",
    label: "Start Date",
    required: true,
    visibleWhen: "communicationStartDate",
    span: halfWidth,
    ...lockedProps(locked),
  },
  {
    id: idOf("g4c_enddate", locked),
    type: "datetime",
    name: "g4c_enddate",
    label: "End Date",
    required: true,
    validatorKey: "communicationEndDate",
    minDateKey: "communicationEndDate",
    visibleWhen: "communicationEndDate",
    span: fullWidth,
    ...lockedProps(locked),
  },
  {
    id: idOf("g4c_expirypage", locked),
    type: "text",
    name: "g4c_expirypage",
    label: "Expiry URL",
    helpText:
      "Any attempt to access the email after the end date will redirect to the Expiry URL",
    visibleWhen: "communicationEndDate",
    span: fullWidth,
    ...lockedProps(locked),
  },
  ...(locked
    ? []
    : ([
        {
          id: "field-runat-validation",
          type: "component",
          name: "runat_validation",
          label: "",
          componentKey: "communicationRunAtValidation",
          excludeFromSave: true,
          span: fullWidth,
        },
      ] as PageConfigField[])),
];

// The editable section until the communication is published, then the same as text.
const draftOrLocked = (
  id: string,
  title: string,
  fields: (locked: boolean) => PageConfigField[],
  wizardStep = false
): PageConfigBlock[] => [
  {
    kind: "section",
    id: `section-${id}`,
    title,
    wizardStep,
    visibleWhen: "communicationDraft",
    fields: fields(false),
  },
  {
    kind: "section",
    id: `section-${id}-locked`,
    title,
    visibleWhen: "communicationPublished",
    fields: fields(true),
  },
];

/**
 * Communication as page config. Details, Audience, Test and Publish are the dock's tabs;
 * Setup holds the email's header fields above the design and Plain Text its text version. Name, description and reporting campaign stay editable
 * once published; everything else is text then.
 */
export const communicationPageConfig: PageConfig = {
  schemaVersion: PageConfigSchemaVersion,
  entityName: "g4c_communication",
  viewId: DefaultFormId,
  pageType: "record",
  rows: [
    {
      id: "row-main",
      columns: [
        {
          id: "column-primary",
          span: fullWidth,
          blocks: [
            {
              kind: "section",
              id: "section-details",
              title: "Details",
              wizardStep: true,
              fields: [
                {
                  id: "content-not-activated",
                  kind: "content",
                  type: "text",
                  content: "Ecomms not activated. Communication cannot be edited.",
                  span: fullWidth,
                  visibleWhen: "communicationNotActivated",
                },
                {
                  id: "field-g4c_name",
                  type: "text",
                  name: "g4c_name",
                  label: "Name",
                  required: true,
                  isNameField: true,
                  span: fullWidth,
                },
                {
                  id: "field-g4c_campaignid",
                  type: "lookup",
                  name: "g4c_campaignid",
                  label: "Reporting Campaign",
                  required: true,
                  lookupEntity: "campaign",
                  searchable: true,
                  manageRoute: "/reporting-campaign",
                  validatorKey: "communicationRequiredLookup",
                  span: fullWidth,
                },
                {
                  id: "field-g4c_description",
                  type: "longtext",
                  name: "g4c_description",
                  label: "Description",
                  required: true,
                  rows: 4,
                  span: fullWidth,
                },
                ...communicationTypeFields(false),
                ...communicationTypeFields(true),
                ...dynamicContentFields(false),
                ...dynamicContentFields(true),
              ],
            },
            ...draftOrLocked("audience", "Audience", audienceFields, true),
            ...draftOrLocked("setup", "Setup", setupFields, true),
            {
              kind: "section",
              id: "section-layout",
              title: "Layout",
              wizardStep: true,
              visibleWhen: "communicationLayoutStep",
              fields: [
                {
                  id: "field-layout",
                  type: "component",
                  name: "SelectedTemplate",
                  label: "",
                  componentKey: "communicationLayout",
                  excludeFromSave: true,
                  validatorKey: "communicationLayoutRequired",
                  span: fullWidth,
                },
              ],
            },
            ...draftOrLocked("plaintext", "Plain Text", plainTextFields),
            {
              kind: "section",
              id: "section-test",
              title: "Test",
              visibleWhen: "communicationDraft",
              fields: [
                {
                  id: "field-g4c_testaddress",
                  type: "email",
                  name: "g4c_testaddress",
                  label: "Test Email Address",
                  helpText: "Email address which tests will be sent to",
                  validatorKey: "communicationTestAddress",
                  span: fullWidth,
                },
                {
                  id: "field-g4c_testmarketinglistid",
                  type: "lookup",
                  name: "g4c_testmarketinglistid",
                  label: "Preview Marketing List",
                  helpText:
                    "The static marketing list used to preview the email when testing. Each test email impersonates one of its contacts",
                  lookupEntity: "list",
                  searchable: true,
                  span: fullWidth,
                },
                {
                  id: "field-testAdditionalData",
                  type: "component",
                  name: "testAdditionalData",
                  label: "",
                  componentKey: "communicationTestData",
                  excludeFromSave: true,
                  visibleWhen: "communicationHasBasket",
                  span: fullWidth,
                },
              ],
            },
            ...draftOrLocked("publish", "Publish", publishFields),
          ],
        },
      ],
    },
  ],
};
